import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { softDelete } from '../utils/softDelete';
import GastoModal from './GastoModal';
import { TrashIcon, EyeIcon, EyeOffIcon } from '../components/Icons';
import { onDataChanged } from '../utils/dataBus';

const formatMoneyRaw = (n) => '$' + (n || 0).toLocaleString('es-AR');
const MASK = '$ ••••';
const formatDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR') : '—';
const currentMonthKey = () => new Date().toISOString().substring(0, 7);
const monthLabel = (key) => {
  const [y, m] = key.split('-');
  const l = new Date(+y, +m - 1, 1).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
  return l.charAt(0).toUpperCase() + l.slice(1);
};

export default function GastosDrawer({ open, onClose, onSaved, initialMes = null, ocultar = false }) {
  const [gastos, setGastos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null);
  const [mesFiltro, setMesFiltro] = useState(currentMonthKey());
  const [ocultarLocal, setOcultarLocal] = useState(ocultar);

  const formatMoney = (n) => ocultarLocal ? MASK : formatMoneyRaw(n);

  const showToast = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 2200); };

  const fetchGastos = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('gastos')
      .select('*, clientes(nombre, apellido), expedientes(numero, caratula)')
      .order('fecha', { ascending: false });
    setGastos(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      fetchGastos();
      setMesFiltro(initialMes || currentMonthKey());
      setOcultarLocal(ocultar);
    } else {
      setModalOpen(false);
      setEditing(null);
    }
  }, [open, fetchGastos, initialMes, ocultar]);

  // Mientras está abierto, refrescar ante cualquier escritura en gastos.
  useEffect(() => {
    if (!open) return;
    return onDataChanged(() => fetchGastos(), 'gastos');
  }, [open, fetchGastos]);

  function abrirNuevo() { setEditing(null); setModalOpen(true); }
  function abrirEdicion(g) { setEditing(g); setModalOpen(true); }

  async function eliminar(g) {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    const { error } = await softDelete('gastos', g.id);
    if (error) { showToast('Error al eliminar', 'error'); return; }
    showToast('Gasto enviado a la papelera', 'error');
    fetchGastos();
    onSaved?.();
  }

  function handleSaved() { fetchGastos(); onSaved?.(); }

  if (!open) return null;

  const mesAct = currentMonthKey();
  const totalMes = gastos.filter(g => (g.fecha || '').startsWith(mesAct)).reduce((s, g) => s + (g.monto || 0), 0);
  const mesesPrevios = [...new Set(gastos.map(g => (g.fecha || '').substring(0, 7)).filter(k => k && k !== mesAct))].sort().reverse();
  const visibles = mesFiltro === 'todos' ? gastos : gastos.filter(g => (g.fecha || '').startsWith(mesFiltro));

  const tipoBg = (t) => ({ eventual: '#eff6ff', fijo: '#f3f4f6', deuda: '#fefce8' }[t] || '#f3f4f6');
  const tipoColor = (t) => ({ eventual: '#3b82f6', fijo: '#6b7280', deuda: '#ca8a04' }[t] || '#6b7280');

  const grupos = (() => {
    const map = {};
    visibles.forEach(g => { const k = (g.fecha || '').substring(0, 7) || 'sin'; (map[k] = map[k] || []).push(g); });
    return Object.keys(map).sort().reverse().map(k => ({ key: k, label: k === 'sin' ? 'Sin fecha' : monthLabel(k), items: map[k] }));
  })();

  const renderRow = (g) => (
    <div
      key={g.id}
      onClick={() => abrirEdicion(g)}
      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 8, cursor: 'pointer' }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.concepto || '—'}</div>
        <div style={{ fontSize: 11, color: 'var(--text4)', display: 'flex', gap: 6, alignItems: 'center', marginTop: 2, flexWrap: 'wrap' }}>
          {g.categoria && <><span>{g.categoria}</span><span>·</span></>}
          <span>{formatDate(g.fecha)}</span>
          <span style={{ background: tipoBg(g.tipo), color: tipoColor(g.tipo), padding: '0 6px', borderRadius: 10, fontSize: 10, fontWeight: 600 }}>{g.tipo}</span>
          <span>·</span><span>{g.medio_pago}</span>
        </div>
      </div>
      <div style={{ fontFamily: 'DM Mono', fontSize: 14, fontWeight: 700, color: '#ef4444', flexShrink: 0 }}>{formatMoney(g.monto)}</div>
      <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); eliminar(g); }} style={{ flexShrink: 0, color: 'var(--text4)', display: 'flex', alignItems: 'center' }} title="Eliminar"><TrashIcon /></button>
    </div>
  );

  return (
    <>
      <div className="drawer-overlay" onMouseDown={onClose} />
      <div className="drawer drawer-wide">

        <div className="drawer-header">
          <div className="drawer-header-top">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div>
                <div className="drawer-num">CONTABILIDAD</div>
                <div className="drawer-title">Gastos</div>
              </div>
              <button
                onClick={() => setOcultarLocal(v => !v)}
                title={ocultarLocal ? 'Mostrar cifras' : 'Ocultar cifras'}
                style={{ background: 'none', border: 'none', cursor: 'pointer', lineHeight: 0, padding: 4, color: 'var(--text4)', display: 'flex', alignItems: 'center' }}
              >
                {ocultarLocal ? <EyeOffIcon size={17} /> : <EyeIcon size={17} />}
              </button>
            </div>
            <button className="drawer-close" onClick={onClose}>✕</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ display: 'flex', gap: 18 }}>
              <div>
                <div style={{ fontSize: 10, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Este mes</div>
                <div style={{ fontFamily: 'DM Mono', fontSize: 16, fontWeight: 700, color: '#ef4444' }}>{formatMoney(totalMes)}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Registros (mes)</div>
                <div style={{ fontFamily: 'DM Mono', fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{gastos.filter(g => (g.fecha || '').startsWith(mesAct)).length}</div>
              </div>
            </div>
            <button className="btn btn-primary" onClick={abrirNuevo}>+ Nuevo gasto</button>
          </div>
        </div>

        <div className="drawer-body">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>
              {mesFiltro === 'todos' ? 'Todos los meses' : monthLabel(mesFiltro)}
              <span style={{ color: 'var(--text4)', fontWeight: 400 }}> · {visibles.length}</span>
            </span>
            <select className="form-select" value={mesFiltro} onChange={e => setMesFiltro(e.target.value)} style={{ width: 'auto', fontSize: 12, padding: '5px 10px' }}>
              <option value={mesAct}>Este mes</option>
              {mesesPrevios.map(k => <option key={k} value={k}>{monthLabel(k)}</option>)}
              <option value="todos">Todos los meses</option>
            </select>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text4)', fontSize: 13 }}>Cargando…</div>
          ) : visibles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text4)', fontSize: 13 }}>
              {mesFiltro === 'todos' ? 'No hay gastos registrados.' : 'Sin gastos en este mes.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {grupos.map(grp => (
                <div key={grp.key}>
                  {mesFiltro === 'todos' && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, paddingLeft: 2 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'capitalize', letterSpacing: 0.4 }}>{grp.label}</span>
                      <span style={{ fontSize: 11, fontFamily: 'DM Mono', fontWeight: 700, color: '#ef4444' }}>{formatMoney(grp.items.reduce((s, x) => s + (x.monto || 0), 0))}</span>
                    </div>
                  )}
                  <div style={{ background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 10, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {grp.items.map(g => renderRow(g))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {toast && (
          <div style={{ position: 'absolute', bottom: 18, left: '50%', transform: 'translateX(-50%)', background: toast.tipo === 'error' ? '#fef2f2' : '#f0fdf4', color: toast.tipo === 'error' ? '#b91c1c' : '#15803d', border: `0.5px solid ${toast.tipo === 'error' ? '#fecaca' : '#bbf7d0'}`, padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 500, zIndex: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
            {toast.msg}
          </div>
        )}
      </div>

      <GastoModal open={modalOpen} editing={editing} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={handleSaved} />
    </>
  );
}
